import { faSignature, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Signature, { type SignatureRef } from '@uiw/react-signature';
import { get } from 'lodash';
import { useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Button } from '~/components/ui';
import { helperTextClassNames, labelClassNames } from '~/components/ui/Form/StyledInput';

interface FormularSignatureInputProps {
    name: string;
    label?: string;
    helperText?: string;
}

// Rounds every coordinate in each <path d="…"> to 1 decimal so the serialized SVG stays small.
// The raw library output uses ~15-decimal floats, which balloons the payload and overflows the DB column.
const compressSvgPaths = (svg: SVGSVGElement) => {
    svg.querySelectorAll('path[d]').forEach(path => {
        const d = path.getAttribute('d');
        if (d) {
            path.setAttribute(
                'd',
                d.replace(/-?\d*\.\d+/g, match => String(Math.round(parseFloat(match) * 10) / 10))
            );
        }
    });
};

// Renders a signature pad and stores the drawn signature as a size-optimised SVG string.
const FormularSignatureInput = ({ name, label, helperText }: FormularSignatureInputProps) => {
    const {
        control,
        formState: { errors }
    } = useFormContext();
    const sigRef = useRef<SignatureRef>(null);
    const [isSigning, setIsSigning] = useState(false);
    const error = get(errors, name);

    return (
        <div className="tw-my-1 tw-w-full">
            {label && <label className={labelClassNames({ isInvalid: !!error })}>{label}</label>}
            <Controller
                control={control}
                name={name}
                defaultValue={null}
                render={({ field: { onChange, value } }) => (
                    <div className="tw-flex tw-flex-col tw-gap-2">
                        <div
                            className={[
                                'tw-relative tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid',
                                error ? 'tw-border-error tw-bg-errorLight' : 'tw-border-transparent tw-bg-accents-0'
                            ].join(' ')}
                        >
                            {!value && !isSigning && (
                                <div className="tw-pointer-events-none tw-absolute tw-inset-0 tw-z-10 tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-text-accents-7">
                                    <FontAwesomeIcon icon={faSignature} className="tw-text-2xl" />
                                    <span className="tw-text-sm">Hier unterschreiben mit Maus oder Finger</span>
                                </div>
                            )}
                            <Signature
                                ref={sigRef}
                                style={{ width: '100%', height: 200, cursor: 'crosshair' }}
                                onPointerDown={() => {
                                    setIsSigning(true);
                                }}
                                onPointerUp={() => {
                                    setIsSigning(false);
                                }}
                                onPointer={() => {
                                    // slight delay so the SVG DOM is updated before we read it
                                    requestAnimationFrame(() => {
                                        const svg = sigRef.current?.svg;
                                        if (svg) {
                                            const clone = svg.cloneNode(true) as SVGSVGElement;
                                            // Strip class attributes — they reference browser stylesheets unavailable in PDF renderers
                                            clone.removeAttribute('class');
                                            clone.querySelectorAll('[class]').forEach(el => el.removeAttribute('class'));
                                            // Reduce coordinate precision to keep the stored SVG small
                                            compressSvgPaths(clone);
                                            // Set viewBox from actual pixel dimensions so PDF renderers can scale correctly
                                            const { width, height } = svg.getBoundingClientRect();
                                            clone.setAttribute('viewBox', `0 0 ${Math.round(width)} ${Math.round(height)}`);
                                            clone.setAttribute('width', String(Math.round(width)));
                                            clone.setAttribute('height', String(Math.round(height)));
                                            clone.style.cssText = '';
                                            onChange(clone.outerHTML);
                                        }
                                    });
                                }}
                            />
                            {value && !isSigning && (
                                <Button
                                    className="tw-absolute tw-right-2 tw-top-2"
                                    color="error"
                                    onClick={() => {
                                        sigRef.current?.clear();
                                        setIsSigning(false);
                                        onChange(null);
                                    }}
                                >
                                    <FontAwesomeIcon icon={faTrash} />
                                </Button>
                            )}
                        </div>
                    </div>
                )}
            />

            {(error?.message || helperText) && (
                <span className={helperTextClassNames({ isInvalid: !!error })}>
                    {(error?.message as string) || helperText}
                </span>
            )}
        </div>
    );
};

export default FormularSignatureInput;
